import React, { useEffect, useState, useMemo, useRef } from 'react'
import { supabase } from '../../supabaseClient'
import {
    BarChart, Bar, PieChart, Pie,
    XAxis, YAxis, CartesianGrid, Tooltip,
    ResponsiveContainer, Cell
} from "recharts"

const C = {
    espresso: "transparent",
    dark: "#F9FAFB",
    card: "#FFFFFF",
    border: "#E5E7EB",
    muted: "#9CA3AF",
    text: "#111827",
    textSoft: "#6B7280",
    amber: "#D97706",
    gold: "#B45309",
    teal: "#0D9488",
    coral: "#DC2626",
    blue: "#2563EB",
    green: "#16A34A",
    purple: "#7C3AED",
}

const BRANCH_COLORS = [C.amber, C.teal, C.blue, C.coral, C.green]
const FULFILLMENT_COLORS = { dine_in: C.teal, takeout: C.amber, pickup: C.blue, delivery: C.green }
const SOURCE_COLORS = { pos: C.purple, mobile_app: C.teal }

const fmt = (n) => `₱${Number(n).toLocaleString("en-PH", { minimumFractionDigits: 0 })}`
const fmtFull = (n) => `₱${Number(n).toLocaleString("en-PH", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
const PHT_OFFSET_MS = 8 * 60 * 60 * 1000
const toPHT = (iso) => new Date(new Date(iso).getTime() + PHT_OFFSET_MS)
const fmtTime = (d) => d.toLocaleTimeString("en-PH", { hour: "2-digit", minute: "2-digit", timeZone: "UTC" })
const fmtDateTime = (iso) => {
    const d = toPHT(iso)
    return d.toLocaleDateString("en-PH", { month: "short", day: "numeric", timeZone: "UTC" }) +
        " " + fmtTime(d)
}
const toLocalDateInput = (date) => {
    const d = new Date(date.getTime() + PHT_OFFSET_MS)
    return d.toISOString().slice(0, 10)
}
const toLocalTimeInput = (date) => {
    const d = new Date(date.getTime() + PHT_OFFSET_MS)
    return d.toISOString().slice(11, 16)
}

// ─── Shared UI components ─────────────────────────────────────────────────────
const Card = ({ children, className = "", style = {} }) => (
    <div className={`rounded-2xl border p-5 ${className}`}
        style={{ background: C.card, borderColor: C.border, boxShadow: "0 1px 4px rgba(0,0,0,0.05)", ...style }}>
        {children}
    </div>
)

const SectionTitle = ({ title, subtitle }) => (
    <div className="mb-5">
        <h2 className="m-0 text-[17px] font-bold tracking-tight" style={{ color: C.text }}>{title}</h2>
        {subtitle && <p className="m-0 text-xs mt-0.5" style={{ color: C.textSoft }}>{subtitle}</p>}
    </div>
)

const KPI = ({ label, value, sub, color = C.amber }) => (
    <div className="rounded-xl p-3.5 min-w-0 border" style={{ background: "#F3F4F6", borderColor: C.border }}>
        <p className="m-0 text-[11px] uppercase tracking-widest" style={{ color: C.muted }}>{label}</p>
        <p className="mt-1 mb-0.5 text-[22px] font-bold" style={{ color }}>{value}</p>
        {sub && <p className="m-0 text-[11px]" style={{ color: C.textSoft }}>{sub}</p>}
    </div>
)

const EmptyState = ({ message = "No data available.", height = 160 }) => (
    <div className="flex flex-col items-center justify-center gap-2 rounded-xl border border-dashed"
        style={{ height, borderColor: C.border }}>
        <p className="m-0 text-[13px] text-center max-w-[256px]" style={{ color: C.muted }}>{message}</p>
    </div>
)

const Spinner = ({ height = 120 }) => (
    <div className="flex items-center justify-center gap-2" style={{ height }}>
        <svg className="animate-spin w-5 h-5" viewBox="0 0 24 24" fill="none">
            <circle cx="12" cy="12" r="10" stroke={C.border} strokeWidth="4" />
            <path fill={C.amber} d="M4 12a8 8 0 018-8v8z" />
        </svg>
        <span className="text-xs" style={{ color: C.muted }}>Loading…</span>
    </div>
)

const Badge = ({ label, color = C.muted }) => (
    <span className="text-[10px] px-2 py-0.5 rounded-full border font-semibold"
        style={{ background: color + "15", borderColor: color + "33", color }}>
        {label}
    </span>
)

const AXIS_TICK = { fill: C.textSoft, fontSize: 11, fontFamily: "inherit" }
const AXIS_TICK_SM = { fill: C.muted, fontSize: 10, fontFamily: "inherit" }
const GRID_PROPS = { stroke: "#F3F4F6", strokeDasharray: "4 4", vertical: false }

// ─── Data hooks ───────────────────────────────────────────────────────────────
function useBranches() {
    const [branches, setBranches] = useState(null)
    useEffect(() => {
        supabase
            .from("branches")
            .select("branch_id, name, status")
            .neq("status", "inactive")
            .order("name")
            .then(({ data }) => {
                const EXCLUDED = ['Alea Residences', 'Asteria Residences', 'Calathea Place', 'HAU']
                setBranches((data ?? []).filter(b => !EXCLUDED.some(ex => b.name.includes(ex))))
            })
    }, [])
    return branches
}

function useStaff() {
    const [staff, setStaff] = useState(null)
    useEffect(() => {
        supabase
            .from("staff")
            .select("id, first_name, last_name, role, branch_id")
            .eq("status", "Active")
            .then(({ data }) => setStaff(data ?? []))
    }, [])
    return staff
}

function useShiftOrders(branchId, startISO, endISO) {
    const [data, setData] = useState(null)
    useEffect(() => {
        if (!branchId || !startISO || !endISO) { setData([]); return }
        let cancelled = false
        setData(null)
        supabase
            .from("orders")
            .select(`
                order_id, reference_number, final_amount, subtotal, discount,
                order_status, order_source, fulfillment_type, created_at,
                completed_at, staff_id, pos_processed_by, order_completed_by,
                barista_id, branch_id
            `)
            .eq("branch_id", branchId)
            .gte("created_at", startISO)
            .lte("created_at", endISO)
            .order("created_at", { ascending: true })
            .then(({ data: rows, error }) => {
                if (cancelled) return
                if (error) { console.error(error); setData([]); return }
                setData(rows ?? [])
            })
        return () => { cancelled = true }
    }, [branchId, startISO, endISO])
    return data
}
async function generateShiftExcel({ orders, branchName, shiftDate, shiftStart, shiftEnd, staffMap }) {
    if (!window.ExcelJS) {
        await new Promise((resolve, reject) => {
            const script = document.createElement("script")
            script.src = "https://cdnjs.cloudflare.com/ajax/libs/exceljs/4.4.0/exceljs.min.js"
            script.onload = resolve
            script.onerror = reject
            document.head.appendChild(script)
        })
    }

    const wb = new window.ExcelJS.Workbook()
    wb.creator = "Coffee Gus·to"
    wb.created = new Date()

    // ── Shift Report palette (distinct from Reports & Analytics) ───────────
    const HEADER_BG = "FF0F172A"   // dark slate/navy
    const BRAND_TEAL = "FF0D9488"  // primary accent
    const BRAND_BLUE = "FF2563EB"
    const BRAND_CORAL = "FFDC2626"
    const BRAND_GREEN = "FF16A34A"
    const BRAND_GOLD = "FFB45309"
    const ALT_ROW_BG = "FFF0FDFA"  // light teal tint
    const TOTAL_BG = "FFCCFBF1"    // teal tint
    const WHITE = "FFFFFFFF"

    const headerStyle = (bgColor = HEADER_BG) => ({
        font: { name: "Arial", bold: true, color: { argb: WHITE }, size: 10 },
        fill: { type: "pattern", pattern: "solid", fgColor: { argb: bgColor } },
        alignment: { horizontal: "center", vertical: "middle", wrapText: true },
        border: { bottom: { style: "medium", color: { argb: BRAND_TEAL } } },
    })

    const dataStyle = (numFmt, color = "FF111827", bold = false) => ({
        font: { name: "Arial", size: 10, color: { argb: color }, bold },
        alignment: { horizontal: "right", vertical: "middle" },
        numFmt,
    })

    const labelStyle = {
        font: { name: "Arial", size: 10, color: { argb: "FF374151" } },
        alignment: { horizontal: "left", vertical: "middle" },
    }

    const totalRowStyle = (numFmt) => ({
        font: { name: "Arial", bold: true, size: 10, color: { argb: "FF0F766E" } },
        fill: { type: "pattern", pattern: "solid", fgColor: { argb: TOTAL_BG } },
        alignment: { horizontal: "right", vertical: "middle" },
        border: { top: { style: "thin", color: { argb: BRAND_TEAL } } },
        numFmt,
    })

    const addSheetTitle = (ws, title, subtitle, colCount) => {
        ws.mergeCells(1, 1, 1, colCount)
        const titleCell = ws.getCell("A1")
        titleCell.value = `Coffee Gus·to — ${title}`
        titleCell.font = { name: "Arial", bold: true, size: 14, color: { argb: WHITE } }
        titleCell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: HEADER_BG } }
        titleCell.alignment = { horizontal: "left", vertical: "middle", indent: 1 }
        ws.getRow(1).height = 32

        ws.mergeCells(2, 1, 2, colCount)
        const subCell = ws.getCell("A2")
        subCell.value = subtitle
        subCell.font = { name: "Arial", italic: true, size: 9, color: { argb: "FF6B7280" } }
        subCell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FFF3F4F6" } }
        subCell.alignment = { horizontal: "left", vertical: "middle", indent: 1 }
        ws.getRow(2).height = 18
    }

    const applyAltRows = (ws, startRow, endRow, colCount, skipCols = []) => {
        for (let r = startRow; r <= endRow; r++) {
            if (r % 2 === 0) {
                for (let c = 1; c <= colCount; c++) {
                    if (skipCols.includes(c)) continue
                    ws.getCell(r, c).fill = { type: "pattern", pattern: "solid", fgColor: { argb: ALT_ROW_BG } }
                }
            }
        }
    }

    const fmt$ = (n) => Number(n ?? 0)
    const getStaffName = (id) => {
        if (!id || !staffMap[id]) return null
        const s = staffMap[id]
        return `${s.first_name} ${s.last_name}`
    }
    const toPhT = (iso) => iso
        ? new Date(new Date(iso).getTime() + 8 * 3600000)
            .toLocaleString("en-PH", { timeZone: "UTC", year: "numeric", month: "short", day: "numeric", hour: "2-digit", minute: "2-digit", second: "2-digit" })
        : ""

    const completed = orders.filter(o => o.order_status === "completed")
    const cancelled = orders.filter(o => o.order_status === "cancelled")
    const totalRevenue = completed.reduce((s, o) => s + Number(o.final_amount), 0)
    const totalDiscount = completed.reduce((s, o) => s + Number(o.discount), 0)
    const avgTicket = completed.length > 0 ? totalRevenue / completed.length : 0
    const grossRevenue = completed.reduce((s, o) => s + Number(o.subtotal ?? o.final_amount), 0)
    const cancelPct = orders.length > 0 ? ((cancelled.length / orders.length) * 100).toFixed(1) : "0.0"

    const branchLabel = branchName || "—"
    const exportDate = new Date().toISOString().split("T")[0]

    // ── SUMMARY ──────────────────────────────────────────────────────────
    const summaryWs = wb.addWorksheet("Summary")
    summaryWs.columns = [{ width: 30 }, { width: 22 }, { width: 22 }]
    addSheetTitle(summaryWs, "End of Shift Report",
        `Branch: ${branchLabel}  ·  ${shiftDate}  ·  ${shiftStart}–${shiftEnd} PHT  ·  Exported: ${exportDate}`, 3)

    const hRow = summaryWs.getRow(4)
    hRow.values = ["Metric", "Value", "Notes"]
    hRow.height = 22
        ;[1, 2, 3].forEach(ci => Object.assign(hRow.getCell(ci), headerStyle()))

    const summaryRows = [
        ["Gross Revenue (subtotal)", grossRevenue, "currency"],
        ["Total Discounts Applied", totalDiscount, "currency"],
        ["Net Revenue Collected", totalRevenue, "currency"],
        ["Completed Orders", completed.length, "count"],
        ["Cancelled Orders", cancelled.length, "count"],
        ["Cancellation Rate", `${cancelPct}%`, "text"],
        ["Average Ticket Size", avgTicket, "currency"],
        ["Total Orders in Window", orders.length, "count"],
    ]

    summaryRows.forEach((row, i) => {
        const r = summaryWs.getRow(5 + i)
        r.height = 20
        r.getCell(1).value = row[0]; Object.assign(r.getCell(1), labelStyle)
        const fmtMap = { currency: "₱#,##0.00", count: "#,##0", text: "@" }
        r.getCell(2).value = row[1]
        Object.assign(r.getCell(2), dataStyle(fmtMap[row[2]] ?? "@", BRAND_TEAL, true))
        r.getCell(3).value = ""
        Object.assign(r.getCell(3), labelStyle)
    })
    applyAltRows(summaryWs, 5, 4 + summaryRows.length, 3)

    // ── FULFILLMENT BREAKDOWN ───────────────────────────────────────────
    const FULFILLMENT_LABELS = { dine_in: "Dine-in", takeout: "Takeout", pickup: "Pick-up", delivery: "Delivery" }
    const byFulfillment = Object.entries(
        completed.reduce((acc, o) => {
            const k = o.fulfillment_type ?? "unknown"
            if (!acc[k]) acc[k] = { count: 0, revenue: 0 }
            acc[k].count++; acc[k].revenue += Number(o.final_amount)
            return acc
        }, {})
    ).sort((a, b) => b[1].revenue - a[1].revenue)

    if (byFulfillment.length) {
        const ws = wb.addWorksheet("Fulfillment Breakdown")
        ws.columns = [{ width: 18 }, { width: 12 }, { width: 18 }, { width: 16 }]
        addSheetTitle(ws, "Fulfillment Breakdown", `Branch: ${branchLabel}  ·  ${shiftDate}`, 4)
        const hr = ws.getRow(4)
        hr.values = ["Type", "Orders", "Revenue (₱)", "% of Revenue"]
        hr.height = 22
            ;[1, 2, 3, 4].forEach(ci => Object.assign(hr.getCell(ci), headerStyle(BRAND_BLUE)))
        const total = byFulfillment.reduce((s, [, v]) => s + v.revenue, 0)
        byFulfillment.forEach(([type, v], i) => {
            const r = ws.getRow(5 + i)
            r.height = 18
            r.getCell(1).value = FULFILLMENT_LABELS[type] ?? type; Object.assign(r.getCell(1), labelStyle)
            r.getCell(2).value = v.count; Object.assign(r.getCell(2), dataStyle("#,##0", BRAND_BLUE))
            r.getCell(3).value = fmt$(v.revenue); Object.assign(r.getCell(3), dataStyle("₱#,##0.00", BRAND_TEAL))
            r.getCell(4).value = total > 0 ? v.revenue / total : 0
            Object.assign(r.getCell(4), dataStyle("0.0%", BRAND_GOLD))
        })
        applyAltRows(ws, 5, 4 + byFulfillment.length, 4, [2])
    }

    // ── ORDER SOURCE BREAKDOWN ──────────────────────────────────────────
    const SOURCE_LABELS = { pos: "POS Terminal", mobile_app: "Mobile App" }
    const bySource = Object.entries(
        completed.reduce((acc, o) => {
            const k = o.order_source ?? "unknown"
            if (!acc[k]) acc[k] = { count: 0, revenue: 0 }
            acc[k].count++; acc[k].revenue += Number(o.final_amount)
            return acc
        }, {})
    ).sort((a, b) => b[1].revenue - a[1].revenue)

    if (bySource.length) {
        const ws = wb.addWorksheet("Order Source")
        ws.columns = [{ width: 18 }, { width: 12 }, { width: 18 }, { width: 16 }]
        addSheetTitle(ws, "Order Source Breakdown", `Branch: ${branchLabel}  ·  ${shiftDate}`, 4)
        const hr = ws.getRow(4)
        hr.values = ["Source", "Orders", "Revenue (₱)", "% of Revenue"]
        hr.height = 22
            ;[1, 2, 3, 4].forEach(ci => Object.assign(hr.getCell(ci), headerStyle(BRAND_TEAL)))
        const total = bySource.reduce((s, [, v]) => s + v.revenue, 0)
        bySource.forEach(([src, v], i) => {
            const r = ws.getRow(5 + i)
            r.height = 18
            r.getCell(1).value = SOURCE_LABELS[src] ?? src; Object.assign(r.getCell(1), labelStyle)
            r.getCell(2).value = v.count; Object.assign(r.getCell(2), dataStyle("#,##0", BRAND_BLUE))
            r.getCell(3).value = fmt$(v.revenue); Object.assign(r.getCell(3), dataStyle("₱#,##0.00", BRAND_TEAL))
            r.getCell(4).value = total > 0 ? v.revenue / total : 0
            Object.assign(r.getCell(4), dataStyle("0.0%", BRAND_GOLD))
        })
        applyAltRows(ws, 5, 4 + bySource.length, 4, [2])
    }

    // ── HOURLY REVENUE ───────────────────────────────────────────────────
    const PHT_OFFSET_MS = 8 * 60 * 60 * 1000
    const toPHT = (iso) => new Date(new Date(iso).getTime() + PHT_OFFSET_MS)
    const fmtHour = (h) => h === 0 ? "12am" : h < 12 ? `${h}am` : h === 12 ? "12pm" : `${h - 12}pm`
    const hourlyMap = {}
    for (const o of completed) {
        const h = toPHT(o.created_at).getUTCHours()
        const key = fmtHour(h)
        if (!hourlyMap[key]) hourlyMap[key] = { hour: key, _h: h, orders: 0, revenue: 0 }
        hourlyMap[key].orders++
        hourlyMap[key].revenue += Number(o.final_amount)
    }
    const hourly = Object.values(hourlyMap).sort((a, b) => a._h - b._h)

    if (hourly.length) {
        const ws = wb.addWorksheet("Hourly Revenue")
        ws.columns = [{ width: 14 }, { width: 12 }, { width: 18 }, { width: 18 }]
        addSheetTitle(ws, "Hourly Revenue", `Branch: ${branchLabel}  ·  ${shiftDate}  ·  PHT`, 4)
        const hr = ws.getRow(4)
        hr.values = ["Hour (PHT)", "Orders", "Revenue (₱)", "Avg Ticket (₱)"]
        hr.height = 22
            ;[1, 2, 3, 4].forEach(ci => Object.assign(hr.getCell(ci), headerStyle(BRAND_BLUE)))
        hourly.forEach((h, i) => {
            const r = ws.getRow(5 + i)
            r.height = 18
            r.getCell(1).value = h.hour; Object.assign(r.getCell(1), labelStyle)
            r.getCell(2).value = h.orders; Object.assign(r.getCell(2), dataStyle("#,##0", BRAND_BLUE))
            r.getCell(3).value = fmt$(h.revenue); Object.assign(r.getCell(3), dataStyle("₱#,##0.00", BRAND_TEAL))
            r.getCell(4).value = h.orders ? h.revenue / h.orders : 0
            Object.assign(r.getCell(4), dataStyle("₱#,##0.00", BRAND_GOLD))
        })
        const last = 4 + hourly.length
        const totR = ws.getRow(last + 1)
        totR.height = 22
        totR.getCell(1).value = "TOTAL"; Object.assign(totR.getCell(1), { ...totalRowStyle(""), alignment: { horizontal: "left" } })
        totR.getCell(2).value = { formula: `=SUM(B5:B${last})` }; Object.assign(totR.getCell(2), totalRowStyle("#,##0"))
        totR.getCell(3).value = { formula: `=SUM(C5:C${last})` }; Object.assign(totR.getCell(3), totalRowStyle("₱#,##0.00"))
        totR.getCell(4).value = { formula: `=IFERROR(C${last + 1}/B${last + 1},0)` }; Object.assign(totR.getCell(4), totalRowStyle("₱#,##0.00"))
        applyAltRows(ws, 5, last, 4, [2])
    }

    // ── STAFF ACTIVITY ───────────────────────────────────────────────────
    const staffActivity = {}
    for (const o of completed) {
        const ids = new Set([o.pos_processed_by, o.order_completed_by, o.staff_id].filter(Boolean))
        ids.forEach(id => {
            if (!staffActivity[id]) staffActivity[id] = { id, processed: 0, completed: 0, revenue: 0 }
            if (o.pos_processed_by === id || o.staff_id === id) staffActivity[id].processed++
            if (o.order_completed_by === id) staffActivity[id].completed++
            staffActivity[id].revenue += Number(o.final_amount)
        })
    }
    const staffRows = Object.values(staffActivity).sort((a, b) => b.revenue - a.revenue)

    if (staffRows.length) {
        const ws = wb.addWorksheet("Staff Activity")
        ws.columns = [{ width: 26 }, { width: 16 }, { width: 16 }, { width: 14 }, { width: 18 }]
        addSheetTitle(ws, "Staff Activity", `Branch: ${branchLabel}  ·  ${shiftDate}`, 5)
        const hr = ws.getRow(4)
        hr.values = ["Staff Member", "Role", "Orders Handled", "Completions", "Revenue (₱)"]
        hr.height = 22
            ;[1, 2, 3, 4, 5].forEach(ci => Object.assign(hr.getCell(ci), headerStyle(BRAND_GREEN)))
        staffRows.forEach((s, i) => {
            const r = ws.getRow(5 + i)
            r.height = 18
            const name = getStaffName(s.id) || `Staff ID: ${s.id}`
            r.getCell(1).value = name; Object.assign(r.getCell(1), i === 0 ? { ...labelStyle, font: { ...labelStyle.font, bold: true, color: { argb: BRAND_GOLD } } } : labelStyle)
            r.getCell(2).value = staffMap[s.id]?.role ?? "—"; Object.assign(r.getCell(2), labelStyle)
            r.getCell(3).value = s.processed; Object.assign(r.getCell(3), dataStyle("#,##0", BRAND_BLUE))
            r.getCell(4).value = s.completed; Object.assign(r.getCell(4), dataStyle("#,##0", BRAND_TEAL))
            r.getCell(5).value = fmt$(s.revenue); Object.assign(r.getCell(5), dataStyle("₱#,##0.00", BRAND_GOLD, i === 0))
        })
        applyAltRows(ws, 5, 4 + staffRows.length, 5, [3, 4])
    }

    // ── ORDER LOG (AUDIT TRAIL) ──────────────────────────────────────────
    const ws = wb.addWorksheet("Order Log")
    ws.columns = [{ width: 16 }, { width: 12 }, { width: 12 }, { width: 14 }, { width: 14 }, { width: 14 },
    { width: 14 }, { width: 20 }, { width: 20 }, { width: 18 }, { width: 18 }, { width: 18 }]
    addSheetTitle(ws, "Order Log — Audit Trail", `Branch: ${branchLabel}  ·  ${shiftDate}  ·  ${shiftStart}–${shiftEnd} PHT`, 12)
    const hr = ws.getRow(4)
    hr.values = ["Reference No.", "Status", "Source", "Fulfillment", "Subtotal (₱)", "Discount (₱)",
        "Final Amount (₱)", "Created At (PHT)", "Completed At (PHT)", "Processed By", "Completed By", "Barista"]
    hr.height = 26
    for (let ci = 1; ci <= 12; ci++) Object.assign(hr.getCell(ci), headerStyle())
    orders.forEach((o, i) => {
        const r = ws.getRow(5 + i)
        r.height = 18
        const statusColor = o.order_status === "completed" ? BRAND_GREEN : o.order_status === "cancelled" ? BRAND_CORAL : BRAND_GOLD
        r.getCell(1).value = o.reference_number ?? ""; Object.assign(r.getCell(1), labelStyle)
        r.getCell(2).value = o.order_status ?? ""; Object.assign(r.getCell(2), { ...labelStyle, font: { ...labelStyle.font, color: { argb: statusColor }, bold: true } })
        r.getCell(3).value = o.order_source ?? ""; Object.assign(r.getCell(3), labelStyle)
        r.getCell(4).value = FULFILLMENT_LABELS[o.fulfillment_type] ?? o.fulfillment_type ?? "—"; Object.assign(r.getCell(4), labelStyle)
        r.getCell(5).value = fmt$(o.subtotal ?? o.final_amount ?? 0); Object.assign(r.getCell(5), dataStyle("₱#,##0.00", "FF374151"))
        r.getCell(6).value = fmt$(o.discount ?? 0); Object.assign(r.getCell(6), dataStyle("₱#,##0.00", BRAND_TEAL))
        r.getCell(7).value = fmt$(o.final_amount ?? 0); Object.assign(r.getCell(7), dataStyle("₱#,##0.00", BRAND_GOLD, true))
        r.getCell(8).value = toPhT(o.created_at)
        Object.assign(r.getCell(8), { ...labelStyle, font: { ...labelStyle.font, size: 9, color: { argb: "FF6B7280" } } })
        r.getCell(9).value = toPhT(o.completed_at)
        Object.assign(r.getCell(9), { ...labelStyle, font: { ...labelStyle.font, size: 9, color: { argb: "FF6B7280" } } })
        r.getCell(10).value = getStaffName(o.pos_processed_by || o.staff_id) || ""; Object.assign(r.getCell(10), labelStyle)
        r.getCell(11).value = getStaffName(o.order_completed_by) || ""; Object.assign(r.getCell(11), labelStyle)
        r.getCell(12).value = getStaffName(o.barista_id) || ""; Object.assign(r.getCell(12), labelStyle)
    })
    const last = 4 + orders.length
    const totR = ws.getRow(last + 1)
    totR.height = 22
    totR.getCell(1).value = `TOTAL: ${orders.length} orders (${completed.length} completed, ${cancelled.length} cancelled)`
    ws.mergeCells(last + 1, 1, last + 1, 6)
    Object.assign(totR.getCell(1), { ...totalRowStyle(""), alignment: { horizontal: "left" } })
    totR.getCell(7).value = { formula: `=SUM(G5:G${last})` }; Object.assign(totR.getCell(7), totalRowStyle("₱#,##0.00"))
    applyAltRows(ws, 5, last, 12)

    return wb
}

async function exportShiftReportToPDF({ orders = [], branchName, shiftDate, shiftStart, shiftEnd, staffMap = {} }) {
    const loadScript = (src) => new Promise((resolve, reject) => {
        if (document.querySelector(`script[src="${src}"]`)) { resolve(); return; }
        const s = document.createElement("script");
        s.src = src; s.onload = resolve; s.onerror = reject;
        document.head.appendChild(s);
    });

    try {
        await loadScript("https://cdnjs.cloudflare.com/ajax/libs/jspdf/2.5.1/jspdf.umd.min.js");
        await loadScript("https://cdnjs.cloudflare.com/ajax/libs/jspdf-autotable/3.8.2/jspdf.plugin.autotable.min.js");
    } catch (e) {
        console.error("Failed to load PDF generation libraries.", e);
        return;
    }

    const { jsPDF } = window.jspdf;
    const pdf = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });
    const pageW = pdf.internal.pageSize.getWidth();
    const margin = 12;
    const contentW = pageW - margin * 2;

    const AMBER = [217, 119, 6], TEAL = [13, 148, 136], CORAL = [220, 38, 38];
    const PURPLE = [124, 58, 237], GREEN = [22, 163, 74], DARK = [17, 24, 39], MUTED = [107, 114, 128];

    const fmt = (n) => `P ${Number(n || 0).toLocaleString("en-PH", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    
    const getStaffName = (id) => {
        if (!id || !staffMap[id]) return "—";
        const s = staffMap[id];
        return `${s.first_name || ""} ${s.last_name || ""}`.trim() || "—";
    };

    const toPhT = (iso) => iso
        ? new Date(new Date(iso).getTime() + 8 * 3600000).toLocaleString("en-PH", { timeZone: "UTC", month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" })
        : "—";

    const completed = orders.filter(o => o.order_status === "completed");
    const cancelled = orders.filter(o => o.order_status === "cancelled");
    const totalRevenue = completed.reduce((s, o) => s + Number(o.final_amount || 0), 0);
    const totalDiscount = completed.reduce((s, o) => s + Number(o.discount || 0), 0);
    const grossRevenue = completed.reduce((s, o) => s + Number(o.subtotal ?? o.final_amount ?? 0), 0);
    const avgTicket = completed.length > 0 ? totalRevenue / completed.length : 0;
    const cancelPct = orders.length > 0 ? ((cancelled.length / orders.length) * 100).toFixed(1) : "0.0";

    // Track drawn header pages to prevent duplicate/overlapping headers
    const drawnHeaderPages = new Set();

    const drawHeader = (pageNum) => {
        const page = pageNum || pdf.internal.getNumberOfPages();
        if (drawnHeaderPages.has(page)) return;
        drawnHeaderPages.add(page);

        pdf.setPage(page);
        const isFirst = page === 1;

        pdf.setFillColor(...AMBER);
        pdf.rect(margin, margin, contentW, 1, "F");
        pdf.setFont("helvetica", "bold"); pdf.setFontSize(13); pdf.setTextColor(...AMBER);
        pdf.text("Coffee Gus\u00B7to", margin, margin + 7);
        pdf.setFont("helvetica", "normal"); pdf.setFontSize(8.5); pdf.setTextColor(...MUTED);
        pdf.text(isFirst ? "End of Shift Report" : "End of Shift Report (cont.)", pageW - margin, margin + 7, { align: "right" });
        pdf.setFontSize(7.5); pdf.setTextColor(156, 163, 175);
        const shiftTime = shiftStart && shiftEnd ? `${shiftStart}\u2013${shiftEnd} PHT` : "";
        const sub = [branchName || "All Branches", shiftDate, shiftTime].filter(Boolean).join("  \u00B7  ");
        pdf.text(sub, margin, margin + 12);
        pdf.setDrawColor(229, 231, 235); pdf.setLineWidth(0.3);
        pdf.line(margin, margin + 16, pageW - margin, margin + 16);
    };

    const sectionTitle = (yPos, title, color) => {
        pdf.setFillColor(...color);
        pdf.rect(margin, yPos, 3, 5, "F");
        pdf.setFont("helvetica", "bold"); pdf.setFontSize(10.5); pdf.setTextColor(...DARK);
        pdf.text(title, margin + 6, yPos + 4);
        return yPos + 8;
    };

    const ensureSpace = (needed) => {
        const pageH = pdf.internal.pageSize.getHeight();
        if (y + needed > pageH - 16) {
            pdf.addPage();
            drawHeader(pdf.internal.getNumberOfPages());
            y = margin + 22;
        }
    };

    const tableDefaults = {
        margin: { left: margin, right: margin },
        theme: "grid",
        styles: { font: "helvetica", fontSize: 8, cellPadding: 2.5, textColor: [55, 65, 81] },
        headStyles: { fontSize: 8.5, fontStyle: "bold", textColor: [255, 255, 255] },
        alternateRowStyles: { fillColor: [249, 250, 251] },
        didDrawPage: (data) => {
            drawHeader(data.pageNumber);
        },
    };

    drawHeader(1);
    let y = margin + 22;

    // 1. Shift Summary
    y = sectionTitle(y, "Shift Summary", AMBER);
    pdf.autoTable({
        ...tableDefaults,
        startY: y,
        head: [["Metric", "Value"]],
        body: [
            ["Gross Revenue (subtotal)", fmt(grossRevenue)],
            ["Total Discounts Applied", fmt(totalDiscount)],
            ["Net Revenue Collected", fmt(totalRevenue)],
            ["Completed Orders", `${completed.length}`],
            ["Cancelled Orders", `${cancelled.length}`],
            ["Cancellation Rate", `${cancelPct}%`],
            ["Average Ticket Size", fmt(avgTicket)],
            ["Total Orders in Window", `${orders.length}`],
        ],
        headStyles: { ...tableDefaults.headStyles, fillColor: DARK },
        columnStyles: { 1: { halign: "right", fontStyle: "bold", textColor: TEAL } },
    });
    y = pdf.lastAutoTable.finalY + 8;

    // 2. Fulfillment Breakdown
    const FULFILLMENT_LABELS = { dine_in: "Dine-in", takeout: "Takeout", pickup: "Pick-up", delivery: "Delivery" };
    const byFulfillment = Object.entries(
        completed.reduce((acc, o) => {
            const k = o.fulfillment_type ?? "unknown";
            if (!acc[k]) acc[k] = { count: 0, revenue: 0 };
            acc[k].count++; acc[k].revenue += Number(o.final_amount || 0);
            return acc;
        }, {})
    ).sort((a, b) => b[1].revenue - a[1].revenue);

    if (byFulfillment.length) {
        ensureSpace(25);
        y = sectionTitle(y, "Fulfillment Breakdown", TEAL);
        const total = byFulfillment.reduce((s, [, v]) => s + v.revenue, 0);
        pdf.autoTable({
            ...tableDefaults,
            startY: y,
            head: [["Type", "Orders", "Revenue", "% of Revenue"]],
            body: byFulfillment.map(([type, v]) => [
                FULFILLMENT_LABELS[type] ?? type, v.count, fmt(v.revenue),
                total > 0 ? `${((v.revenue / total) * 100).toFixed(1)}%` : "0%"
            ]),
            headStyles: { ...tableDefaults.headStyles, fillColor: TEAL },
            columnStyles: { 1: { halign: "center" }, 2: { halign: "right" }, 3: { halign: "right" } },
        });
        y = pdf.lastAutoTable.finalY + 8;
    }

    // 3. Order Source Breakdown
    const SOURCE_LABELS = { pos: "POS Terminal", mobile_app: "Mobile App" };
    const bySource = Object.entries(
        completed.reduce((acc, o) => {
            const k = o.order_source ?? "unknown";
            if (!acc[k]) acc[k] = { count: 0, revenue: 0 };
            acc[k].count++; acc[k].revenue += Number(o.final_amount || 0);
            return acc;
        }, {})
    ).sort((a, b) => b[1].revenue - a[1].revenue);

    if (bySource.length) {
        ensureSpace(25);
        y = sectionTitle(y, "Order Source Breakdown", PURPLE);
        const total = bySource.reduce((s, [, v]) => s + v.revenue, 0);
        pdf.autoTable({
            ...tableDefaults,
            startY: y,
            head: [["Source", "Orders", "Revenue", "% of Revenue"]],
            body: bySource.map(([src, v]) => [
                SOURCE_LABELS[src] ?? src, v.count, fmt(v.revenue),
                total > 0 ? `${((v.revenue / total) * 100).toFixed(1)}%` : "0%"
            ]),
            headStyles: { ...tableDefaults.headStyles, fillColor: PURPLE },
            columnStyles: { 1: { halign: "center" }, 2: { halign: "right" }, 3: { halign: "right" } },
        });
        y = pdf.lastAutoTable.finalY + 8;
    }

    // 4. Staff Activity
    const staffActivity = {};
    for (const o of completed) {
        const ids = new Set([o.pos_processed_by, o.order_completed_by, o.staff_id].filter(Boolean));
        ids.forEach(id => {
            if (!staffActivity[id]) staffActivity[id] = { id, processed: 0, completed: 0, revenue: 0 };
            if (o.pos_processed_by === id || o.staff_id === id) staffActivity[id].processed++;
            if (o.order_completed_by === id) staffActivity[id].completed++;
            staffActivity[id].revenue += Number(o.final_amount || 0);
        });
    }
    const staffRows = Object.values(staffActivity).sort((a, b) => b.revenue - a.revenue);

    if (staffRows.length) {
        ensureSpace(25);
        y = sectionTitle(y, "Staff Activity", GREEN);
        pdf.autoTable({
            ...tableDefaults,
            startY: y,
            head: [["Staff Member", "Role", "Orders Handled", "Completions", "Revenue"]],
            body: staffRows.map(s => [
                getStaffName(s.id), staffMap[s.id]?.role ?? "—", s.processed, s.completed, fmt(s.revenue)
            ]),
            headStyles: { ...tableDefaults.headStyles, fillColor: GREEN },
            columnStyles: { 2: { halign: "center" }, 3: { halign: "center" }, 4: { halign: "right" } },
        });
        y = pdf.lastAutoTable.finalY + 8;
    }

    // 5. Order Log
    if (orders.length) {
        ensureSpace(25);
        y = sectionTitle(y, "Order Log", DARK);
        pdf.autoTable({
            ...tableDefaults,
            startY: y,
            head: [["Reference", "Status", "Source", "Type", "Amount", "Created At", "Processed By"]],
            body: orders.map(o => [
                o.reference_number ?? "—",
                o.order_status ?? "—",
                o.order_source ?? "—",
                FULFILLMENT_LABELS[o.fulfillment_type] ?? o.fulfillment_type ?? "—",
                fmt(o.final_amount),
                toPhT(o.created_at),
                getStaffName(o.pos_processed_by || o.staff_id),
            ]),
            headStyles: { ...tableDefaults.headStyles, fillColor: DARK },
            styles: { ...tableDefaults.styles, fontSize: 7.5, cellPadding: 2 },
            columnStyles: { 4: { halign: "right" } },
            didParseCell: (data) => {
                if (data.section === "body" && data.column.index === 1) {
                    const status = data.cell.raw;
                    if (status === "completed") data.cell.styles.textColor = GREEN;
                    if (status === "cancelled") data.cell.styles.textColor = CORAL;
                }
            },
        });
        y = pdf.lastAutoTable.finalY + 8;
    }

    // Footer & Page Numbers
    const totalPages = pdf.internal.getNumberOfPages();
    for (let i = 1; i <= totalPages; i++) {
        pdf.setPage(i);
        const pageH = pdf.internal.pageSize.getHeight();
        pdf.setFont("helvetica", "normal"); pdf.setFontSize(7.5); pdf.setTextColor(...MUTED);
        pdf.text("Coffee Gus\u00B7to \u2014 Confidential", margin, pageH - 8);
        pdf.text(`Page ${i} of ${totalPages}`, pageW - margin, pageH - 8, { align: "right" });
    }

    const dateStr = shiftDate ? shiftDate.replace(/\s+/g, "_") : new Date().toISOString().split("T")[0];
    const branchSlug = branchName ? `_${branchName.replace(/\s+/g, "_")}` : "_AllBranches";
    pdf.save(`CoffeeGusto_ShiftReport${branchSlug}_${dateStr}.pdf`);
}

// ─── Sub-components ───────────────────────────────────────────────────────────
const FulfillmentBreakdown = ({ orders }) => {
    const completed = orders.filter(o => o.order_status === "completed")
    const byType = Object.entries(
        completed.reduce((acc, o) => {
            const k = o.fulfillment_type ?? "unknown"
            if (!acc[k]) acc[k] = { name: k, count: 0, revenue: 0 }
            acc[k].count++
            acc[k].revenue += Number(o.final_amount)
            return acc
        }, {})
    ).map(([, v]) => v).sort((a, b) => b.revenue - a.revenue)

    const LABELS = { dine_in: "Dine-in", takeout: "Takeout", pickup: "Pick-up", delivery: "Delivery", unknown: "—" }

    return (
        <div>
            <p className="text-[11px] uppercase tracking-wider mb-3 m-0" style={{ color: C.muted }}>
                By Fulfillment Type
            </p>
            {byType.length === 0 ? (
                <EmptyState message="No completed orders." height={100} />
            ) : (
                <div className="flex flex-col gap-1.5">
                    {byType.map((t) => {
                        const color = FULFILLMENT_COLORS[t.name] || C.muted
                        const total = byType.reduce((s, x) => s + x.revenue, 0)
                        const pct = total > 0 ? ((t.revenue / total) * 100).toFixed(1) : "0"
                        return (
                            <div key={t.name}
                                className="flex items-center justify-between px-3 py-2 rounded-lg"
                                style={{ background: C.dark, border: `1px solid ${C.border}` }}>
                                <div className="flex items-center gap-2">
                                    <div style={{ width: 8, height: 8, borderRadius: "50%", background: color, flexShrink: 0 }} />
                                    <span style={{ fontSize: 12, color: C.text }}>{LABELS[t.name] ?? t.name}</span>
                                </div>
                                <div className="flex items-center gap-3">
                                    <span style={{ fontSize: 11, color: C.muted }}>{t.count} orders</span>
                                    <span style={{ fontSize: 11, color: C.muted }}>{pct}%</span>
                                    <span style={{ fontSize: 12, fontWeight: 600, color, minWidth: 72, textAlign: "right" }}>{fmt(t.revenue)}</span>
                                </div>
                            </div>
                        )
                    })}
                </div>
            )}
        </div>
    )
}

const SourceBreakdown = ({ orders }) => {
    const completed = orders.filter(o => o.order_status === "completed")
    const bySource = Object.entries(
        completed.reduce((acc, o) => {
            const k = o.order_source ?? "unknown"
            if (!acc[k]) acc[k] = { name: k, count: 0, revenue: 0 }
            acc[k].count++
            acc[k].revenue += Number(o.final_amount)
            return acc
        }, {})
    ).map(([, v]) => v).sort((a, b) => b.revenue - a.revenue)

    const LABELS = { pos: "POS Terminal", mobile_app: "Mobile App", unknown: "Unknown" }
    const total = bySource.reduce((s, x) => s + x.revenue, 0)

    return (
        <div>
            <p className="text-[11px] uppercase tracking-wider mb-3 m-0" style={{ color: C.muted }}>
                By Order Source
            </p>
            {bySource.length === 0 ? (
                <EmptyState message="No completed orders." height={100} />
            ) : (
                <div className="flex flex-col gap-1.5">
                    {bySource.map((s) => {
                        const color = SOURCE_COLORS[s.name] || C.muted
                        const pct = total > 0 ? ((s.revenue / total) * 100).toFixed(1) : "0"
                        return (
                            <div key={s.name}
                                className="flex items-center justify-between px-3 py-2 rounded-lg"
                                style={{ background: color + "08", border: `1px solid ${color}22` }}>
                                <div className="flex items-center gap-2">
                                    <div style={{ width: 8, height: 8, borderRadius: "50%", background: color, flexShrink: 0 }} />
                                    <span style={{ fontSize: 12, color: C.text }}>{LABELS[s.name] ?? s.name}</span>
                                </div>
                                <div className="flex items-center gap-3">
                                    <span style={{ fontSize: 11, color: C.muted }}>{s.count} orders</span>
                                    <span style={{ fontSize: 11, color: C.muted }}>{pct}%</span>
                                    <span style={{ fontSize: 12, fontWeight: 600, color, minWidth: 72, textAlign: "right" }}>{fmt(s.revenue)}</span>
                                </div>
                            </div>
                        )
                    })}
                </div>
            )}
        </div>
    )
}

const StaffActivityTable = ({ orders, staffMap }) => {
    const getStaffName = (id) => {
        if (!id || !staffMap[id]) return null
        const s = staffMap[id]
        return `${s.first_name} ${s.last_name}`
    }

    // Gather all staff IDs that appear in this shift's orders
    const staffActivity = useMemo(() => {
        const map = {}
        for (const o of orders) {
            if (o.order_status !== "completed") continue
            // Track who processed / completed each order
            const ids = new Set([o.pos_processed_by, o.order_completed_by, o.staff_id].filter(Boolean))
            ids.forEach(id => {
                if (!map[id]) map[id] = { id, processed: 0, completed: 0, revenue: 0 }
                if (o.pos_processed_by === id || o.staff_id === id) map[id].processed++
                if (o.order_completed_by === id) map[id].completed++
                map[id].revenue += Number(o.final_amount)
            })
        }
        return Object.values(map).sort((a, b) => b.revenue - a.revenue)
    }, [orders])

    if (staffActivity.length === 0) return (
        <EmptyState message="No staff activity recorded for this shift." height={100} />
    )

    return (
        <div className="overflow-x-auto">
            <div className="rounded-xl border overflow-hidden" style={{ borderColor: C.border, minWidth: 560 }}>
                <div className="grid px-4 py-2 text-[10px] uppercase tracking-wider"
                    style={{
                        gridTemplateColumns: "1.5fr 1fr 1fr 1fr",
                        background: C.dark, borderBottom: `1px solid ${C.border}`, color: C.muted
                    }}>
                    <span>Staff Member</span>
                    <span style={{ textAlign: "center" }}>Orders Handled</span>
                    <span style={{ textAlign: "center" }}>Completions</span>
                    <span style={{ textAlign: "right" }}>Revenue</span>
                </div>
                {staffActivity.map((s, i) => {
                    const name = getStaffName(s.id)
                    const staffInfo = staffMap[s.id]
                    return (
                        <div key={s.id}
                            className="grid items-center px-4 py-2.5"
                            style={{
                                gridTemplateColumns: "1.5fr 1fr 1fr 1fr",
                                borderBottom: i < staffActivity.length - 1 ? `1px solid ${C.border}` : "none",
                                background: i === 0 ? C.amber + "06" : "transparent",
                            }}>
                            <div>
                                <p className="m-0 text-[12px] font-semibold" style={{ color: i === 0 ? C.gold : C.text }}>
                                    {i === 0 && <span style={{ marginRight: 4 }}>⭐</span>}
                                    {name ?? <span style={{ color: C.muted, fontStyle: "italic" }}>Unknown Staff</span>}
                                </p>
                                {staffInfo && (
                                    <p className="m-0 text-[10px]" style={{ color: C.muted }}>{staffInfo.role}</p>
                                )}
                            </div>
                            <span className="text-center text-[12px] font-bold" style={{ color: C.blue }}>{s.processed}</span>
                            <span className="text-center text-[12px] font-bold" style={{ color: C.teal }}>{s.completed}</span>
                            <span className="text-right text-[12px] font-semibold" style={{ color: C.amber }}>{fmt(s.revenue)}</span>
                        </div>
                    )
                })}
            </div>
        </div>
    )
}

const OrderLog = ({ orders, staffMap }) => {
    const [page, setPage] = useState(1)
    const [filter, setFilter] = useState("all") // all | completed | cancelled
    const PAGE_SIZE = 8

    const getStaffName = (id) => {
        if (!id || !staffMap[id]) return null
        const s = staffMap[id]
        return `${s.first_name} ${s.last_name}`
    }

    const FULFILLMENT_LABELS = { dine_in: "Dine-in", takeout: "Takeout", pickup: "Pick-up", delivery: "Delivery" }
    const STATUS_COLORS = { completed: C.green, cancelled: C.coral, pending: C.amber }

    const filtered = useMemo(() =>
        filter === "all" ? orders : orders.filter(o => o.order_status === filter)
        , [orders, filter])

    useEffect(() => setPage(1), [filter])

    const totalPages = Math.ceil(filtered.length / PAGE_SIZE)
    const paginated = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE)

    return (
        <div>
            {/* Filter tabs */}
            <div className="flex items-center gap-2 mb-3">
                {[
                    { key: "all", label: `All (${orders.length})` },
                    { key: "completed", label: `Completed (${orders.filter(o => o.order_status === "completed").length})` },
                    { key: "cancelled", label: `Cancelled (${orders.filter(o => o.order_status === "cancelled").length})` },
                ].map(f => (
                    <button key={f.key} onClick={() => setFilter(f.key)}
                        className="px-3 py-1 rounded-full text-[11px] border transition-colors"
                        style={{
                            background: filter === f.key ? C.amber : C.card,
                            borderColor: filter === f.key ? C.amber : C.border,
                            color: filter === f.key ? "#fff" : C.textSoft,
                            cursor: "pointer",
                        }}>
                        {f.label}
                    </button>
                ))}
            </div>

            {filtered.length === 0 ? (
                <EmptyState message="No orders match this filter." height={100} />
            ) : (
                <>
                    <div className="overflow-x-auto">
                        <div style={{ minWidth: 640 }}>
                            <div className="rounded-xl border overflow-hidden" style={{ borderColor: C.border }}>
                                {/* Header */}
                                <div className="grid px-4 py-2 text-[10px] uppercase tracking-wider"
                                    style={{
                                        gridTemplateColumns: "1.2fr 1fr 0.8fr 0.8fr 1fr 0.8fr",
                                        background: C.dark, borderBottom: `1px solid ${C.border}`, color: C.muted
                                    }}>
                                    <span>Reference</span>
                                    <span>Time</span>
                                    <span>Source</span>
                                    <span>Type</span>
                                    <span>Processed By</span>
                                    <span style={{ textAlign: "right" }}>Amount</span>
                                </div>

                                {paginated.map((o, i) => {
                                    const statusColor = STATUS_COLORS[o.order_status] || C.muted
                                    const processorId = o.pos_processed_by || o.staff_id
                                    const processorName = getStaffName(processorId)
                                    const isPos = o.order_source === "pos"
                                    return (
                                        <div key={o.order_id}
                                            className="grid items-center px-4 py-2.5"
                                            style={{
                                                gridTemplateColumns: "1.2fr 1fr 0.8fr 0.8fr 1fr 0.8fr",
                                                borderBottom: i < paginated.length - 1 ? `1px solid ${C.border}` : "none",
                                                background: o.order_status === "cancelled" ? C.coral + "05" : "transparent",
                                                opacity: o.order_status === "cancelled" ? 0.75 : 1,
                                            }}>
                                            <div>
                                                <p className="m-0 text-[11px] font-semibold" style={{ color: C.text }}>
                                                    {o.reference_number}
                                                </p>
                                                <div className="flex items-center gap-1 mt-0.5">
                                                    <div style={{ width: 6, height: 6, borderRadius: "50%", background: statusColor, flexShrink: 0 }} />
                                                    <span style={{ fontSize: 10, color: statusColor, textTransform: "capitalize" }}>
                                                        {o.order_status}
                                                    </span>
                                                    {Number(o.discount) > 0 && (
                                                        <span style={{ fontSize: 9, color: C.teal, marginLeft: 4 }}>
                                                            −{fmt(o.discount)} disc.
                                                        </span>
                                                    )}
                                                </div>
                                            </div>
                                            <span style={{ fontSize: 11, color: C.textSoft }}>
                                                {fmtTime(toPHT(o.created_at))}
                                            </span>
                                            <span>
                                                <Badge
                                                    label={isPos ? "POS" : "App"}
                                                    color={isPos ? C.purple : C.teal}
                                                />
                                            </span>
                                            <span style={{ fontSize: 11, color: C.textSoft }}>
                                                {FULFILLMENT_LABELS[o.fulfillment_type] ?? o.fulfillment_type ?? "—"}
                                            </span>
                                            <span style={{ fontSize: 11, color: C.textSoft }}>
                                                {processorName ?? <span style={{ color: C.border }}>—</span>}
                                            </span>
                                            <span className="text-right text-[12px] font-semibold"
                                                style={{ color: o.order_status === "cancelled" ? C.muted : C.amber }}>
                                                {fmt(o.final_amount)}
                                            </span>
                                        </div>
                                    )
                                })}

                                {/* Footer */}
                                <div className="grid px-4 py-2"
                                    style={{
                                        gridTemplateColumns: "1.2fr 1fr 0.8fr 0.8fr 1fr 0.8fr",
                                        background: C.dark, borderTop: `1px solid ${C.border}`,
                                    }}>
                                    <span style={{ fontSize: 11, color: C.muted }}>
                                        {filtered.length} order{filtered.length !== 1 ? "s" : ""}
                                    </span>
                                    <span /><span /><span /><span />
                                    <span className="text-right text-[12px] font-bold" style={{ color: C.amber }}>
                                        {fmt(filtered.filter(o => o.order_status === "completed").reduce((s, o) => s + Number(o.final_amount), 0))}
                                    </span>
                                </div>
                            </div>
                        </div>
                    </div>


                    {/* Pagination */}
                    {totalPages > 1 && (
                        <div className="flex items-center justify-between mt-3">
                            <p className="text-[11px] m-0" style={{ color: C.muted }}>
                                {(page - 1) * PAGE_SIZE + 1}–{Math.min(page * PAGE_SIZE, filtered.length)} of {filtered.length}
                            </p>
                            <div className="flex items-center gap-1">
                                <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}
                                    style={{
                                        padding: "3px 10px", borderRadius: 8, border: `1px solid ${C.border}`,
                                        background: C.card, color: page === 1 ? C.border : C.textSoft,
                                        fontSize: 11, cursor: page === 1 ? "not-allowed" : "pointer",
                                    }}>← Prev</button>
                                {Array.from({ length: totalPages }, (_, i) => i + 1)
                                    .filter(n => n === 1 || n === totalPages || Math.abs(n - page) <= 1)
                                    .reduce((acc, n, idx, arr) => {
                                        if (idx > 0 && n - arr[idx - 1] > 1) acc.push("…")
                                        acc.push(n); return acc
                                    }, [])
                                    .map((n, idx) => n === "…" ? (
                                        <span key={`e-${idx}`} style={{ fontSize: 11, color: C.muted, padding: "0 2px" }}>…</span>
                                    ) : (
                                        <button key={n} onClick={() => setPage(n)}
                                            style={{
                                                width: 28, height: 28, borderRadius: 8,
                                                border: `1px solid ${n === page ? C.amber : C.border}`,
                                                background: n === page ? C.amber : C.card,
                                                color: n === page ? "#fff" : C.textSoft,
                                                fontSize: 11, cursor: "pointer",
                                            }}>{n}</button>
                                    ))}
                                <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages}
                                    style={{
                                        padding: "3px 10px", borderRadius: 8, border: `1px solid ${C.border}`,
                                        background: C.card, color: page === totalPages ? C.border : C.textSoft,
                                        fontSize: 11, cursor: page === totalPages ? "not-allowed" : "pointer",
                                    }}>Next →</button>
                            </div>
                        </div>
                    )}
                </>
            )}
        </div>
    )
}

const HourlyRevenueChart = ({ orders }) => {
    const completed = orders.filter(o => o.order_status === "completed")
    const fmtHour = (h) => {
        if (h === 0) return "12am"
        if (h < 12) return `${h}am`
        if (h === 12) return "12pm"
        return `${h - 12}pm`
    }

    const hourly = useMemo(() => {
        const map = {}
        for (const o of completed) {
            const h = toPHT(o.created_at).getUTCHours()
            const key = fmtHour(h)
            if (!map[key]) map[key] = { hour: key, _h: h, revenue: 0, orders: 0 }
            map[key].revenue += Number(o.final_amount)
            map[key].orders++
        }
        return Object.values(map).sort((a, b) => a._h - b._h)
    }, [completed])

    if (hourly.length === 0) return <EmptyState message="No completed orders to chart." height={160} />

    return (
        <ResponsiveContainer debounce={200} width="100%" height={180}>
            <BarChart data={hourly} margin={{ top: 8, right: 8, left: -15, bottom: 0 }} barCategoryGap="28%">
                <CartesianGrid {...GRID_PROPS} />
                <XAxis dataKey="hour" tick={AXIS_TICK_SM} axisLine={false} tickLine={false} />
                <YAxis tick={AXIS_TICK_SM} tickFormatter={v => `${(v / 1000).toFixed(0)}k`} axisLine={false} tickLine={false} />
                <Tooltip
                    cursor={{ fill: "rgba(0,0,0,0.04)" }}
                    content={({ active, payload, label }) => {
                        if (!active || !payload?.length) return null
                        return (
                            <div style={{
                                background: "#fff", border: `1px solid ${C.border}`,
                                borderRadius: 10, padding: "10px 14px",
                                fontSize: 11, boxShadow: "0 4px 16px rgba(0,0,0,0.10)",
                            }}>
                                <p style={{ margin: "0 0 5px", fontWeight: 700, color: C.text }}>{label}</p>
                                <p style={{ margin: 0, color: C.amber }}>Revenue: <strong>{fmt(payload[0]?.value)}</strong></p>
                                <p style={{ margin: "3px 0 0", color: C.teal }}>Orders: <strong>{payload[0]?.payload?.orders}</strong></p>
                            </div>
                        )
                    }}
                />
                <Bar dataKey="revenue" radius={[5, 5, 0, 0]} maxBarSize={40} fill={C.amber} opacity={0.85} />
            </BarChart>
        </ResponsiveContainer>
    )
}

// ─── Main Component ───────────────────────────────────────────────────────────
export default function EndOfShiftReport() {
    const branches = useBranches()
    const staffList = useStaff()
    const reportRef = React.useRef(null)
    const [pdfLoading, setPdfLoading] = useState(false)

    // Default: today, 6am–6pm PHT
    const now = new Date()
    const defaultDate = toLocalDateInput(now)
    const [selectedBranch, setSelectedBranch] = useState("")
    const [shiftDate, setShiftDate] = useState(defaultDate)
    const [shiftStart, setShiftStart] = useState("06:00")
    const [shiftEnd, setShiftEnd] = useState("18:00")

    const [csvLoading, setCsvLoading] = useState(false)
    const [showDownloadMenu, setShowDownloadMenu] = useState(false)
    const downloadMenuRef = useRef(null)

    useEffect(() => {
        const handleClickOutside = (e) => {
            if (downloadMenuRef.current && !downloadMenuRef.current.contains(e.target)) {
                setShowDownloadMenu(false)
            }
        }
        document.addEventListener('mousedown', handleClickOutside)
        return () => document.removeEventListener('mousedown', handleClickOutside)
    }, [])

    const startISO = useMemo(() => {
        if (!shiftDate || !shiftStart) return null
        return new Date(`${shiftDate}T${shiftStart}:00+08:00`).toISOString()
    }, [shiftDate, shiftStart])

    const endISO = useMemo(() => {
        if (!shiftDate || !shiftEnd) return null
        return new Date(`${shiftDate}T${shiftEnd}:00+08:00`).toISOString()
    }, [shiftDate, shiftEnd])

    const orders = useShiftOrders(selectedBranch, startISO, endISO)
    const loading = orders === null

    const staffMap = useMemo(() => {
        const m = {}
        for (const s of (staffList ?? [])) m[s.id] = s
        return m
    }, [staffList])

    const branchName = useMemo(() => {
        if (!selectedBranch || !branches) return ""
        return branches.find(b => b.branch_id === selectedBranch)?.name?.replace("Coffee Gus·to ", "") ?? ""
    }, [selectedBranch, branches])

    const completed = useMemo(() => (orders ?? []).filter(o => o.order_status === "completed"), [orders])
    const cancelled = useMemo(() => (orders ?? []).filter(o => o.order_status === "cancelled"), [orders])
    const totalRevenue = useMemo(() => completed.reduce((s, o) => s + Number(o.final_amount), 0), [completed])
    const totalDiscount = useMemo(() => completed.reduce((s, o) => s + Number(o.discount), 0), [completed])
    const avgTicket = completed.length > 0 ? totalRevenue / completed.length : 0

    const hasData = orders !== null && orders.length > 0

    const handleDownloadPDF = async () => {
        setPdfLoading(true)
        try {
            await exportShiftReportToPDF({
                orders: orders ?? [],
                branchName,
                shiftDate,
                shiftStart,
                shiftEnd,
                staffMap,
            })
        } catch (e) {
            console.error("PDF export failed:", e)
        } finally {
            setPdfLoading(false)
        }
    }

    const handleDownloadCSV = async () => {
        setCsvLoading(true)
        try {
            const wb = await generateShiftExcel({
                orders: orders ?? [],
                branchName,
                shiftDate,
                shiftStart,
                shiftEnd,
                staffMap,
            })
            const buffer = await wb.xlsx.writeBuffer()
            const blob = new Blob([buffer], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" })
            const url = URL.createObjectURL(blob)
            const a = document.createElement("a")
            a.href = url
            a.download = `CoffeeGusto_ShiftReport_${(branchName || "Branch").replace(/\s+/g, "_")}_${shiftDate}.xlsx`
            document.body.appendChild(a)
            a.click()
            document.body.removeChild(a)
            URL.revokeObjectURL(url)
        } catch (e) {
            console.error("Excel export failed:", e)
        } finally {
            setCsvLoading(false)
        }
    }

    return (
        <div className="p-6 font-sans" style={{ background: C.espresso }}>
            <div className="flex flex-col gap-5" ref={reportRef}>

                <div className="flex items-start justify-between flex-wrap gap-4 pb-4 border-b no-print"
                    style={{ borderColor: C.border }}>
                    <div>
                        <h1 className="m-0 text-xl font-bold tracking-tight" style={{ color: C.text }}>
                            End of Shift Report
                        </h1>
                        <p className="m-0 text-xs mt-1" style={{ color: C.textSoft }}>
                            Sales summary scoped to a single shift window
                        </p>
                    </div>
                    <div className="relative no-print" ref={downloadMenuRef}>
                        <button
                            onClick={() => setShowDownloadMenu(v => !v)}
                            disabled={!hasData}
                            className="px-3 py-1.5 rounded-full text-[11px] border font-semibold transition-colors flex items-center gap-1.5"
                            style={{
                                background: !hasData ? C.border : C.green,
                                borderColor: !hasData ? C.border : C.green,
                                color: "#fff",
                                cursor: !hasData ? "not-allowed" : "pointer"
                            }}>
                            <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24">
                                <path fill="#fff" d="m12 16l-5-5l1.4-1.45l2.6 2.6V4h2v8.15l2.6-2.6L17 11zm-6 4q-.825 0-1.412-.587T4 18v-3h2v3h12v-3h2v3q0 .825-.587 1.413T18 20z" />
                            </svg>
                            Download Report
                        </button>

                        {showDownloadMenu && (
                            <div className="absolute right-0 mt-2 w-44 rounded-lg shadow-lg border z-50"
                                style={{ background: C.card, borderColor: C.border }}>
                                <button
                                    onClick={() => { handleDownloadPDF(); setShowDownloadMenu(false) }}
                                    disabled={pdfLoading}
                                    className="flex items-center gap-2 w-full text-left px-4 py-2.5 text-xs hover:bg-gray-50 rounded-t-lg transition-colors"
                                    style={{ color: C.text, cursor: pdfLoading ? "not-allowed" : "pointer" }}>
                                    {pdfLoading ? (
                                        <>
                                            <svg className="animate-spin w-3 h-3" viewBox="0 0 24 24" fill="none">
                                                <circle cx="12" cy="12" r="10" stroke={C.border} strokeWidth="4" />
                                                <path fill={C.amber} d="M4 12a8 8 0 018-8v8z" />
                                            </svg>
                                            Exporting…
                                        </>
                                    ) : (
                                        <>
                                            <svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 24 24">
                                                <path fill={C.text} d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                                                <polyline points="14 2 14 8 20 8" stroke="#fff" strokeWidth="2" fill="none" />
                                            </svg>
                                            Download as PDF
                                        </>
                                    )}
                                </button>
                                <button
                                    onClick={() => { handleDownloadCSV(); setShowDownloadMenu(false) }}
                                    disabled={csvLoading}
                                    className="flex items-center gap-2 w-full text-left px-4 py-2.5 text-xs hover:bg-gray-50 rounded-b-lg transition-colors border-t"
                                    style={{ color: C.text, borderColor: C.border, cursor: csvLoading ? "not-allowed" : "pointer" }}>
                                    {csvLoading ? (
                                        <>
                                            <svg className="animate-spin w-3 h-3" viewBox="0 0 24 24" fill="none">
                                                <circle cx="12" cy="12" r="10" stroke={C.border} strokeWidth="4" />
                                                <path fill={C.amber} d="M4 12a8 8 0 018-8v8z" />
                                            </svg>
                                            Exporting…
                                        </>
                                    ) : (
                                        <>
                                            <svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 24 24">
                                                <path fill={C.text} d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                                                <polyline points="14 2 14 8 20 8" stroke="#fff" strokeWidth="2" fill="none" />
                                            </svg>
                                            Download as Excel
                                        </>
                                    )}
                                </button>
                            </div>
                        )}
                    </div>
                </div>

                {/* ── Shift Controls ──────────────────────────────────────── */}
                <Card className="no-print">
                    <SectionTitle title="Shift Parameters" subtitle="Select branch and shift window to generate the report" />
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                        {/* Branch */}
                        <div>
                            <label className="block text-[11px] uppercase tracking-wider mb-1.5" style={{ color: C.muted }}>
                                Branch
                            </label>
                            <select
                                value={selectedBranch}
                                onChange={e => setSelectedBranch(e.target.value)}
                                className="w-full rounded-lg border px-2.5 py-2 text-[12px] outline-none"
                                style={{ borderColor: selectedBranch ? C.amber : C.border, color: C.text, background: C.card }}>
                                <option value="">— Select branch —</option>
                                {(branches ?? []).map(b => (
                                    <option key={b.branch_id} value={b.branch_id}>
                                        {b.name.replace("Coffee Gus·to ", "")}
                                    </option>
                                ))}
                            </select>
                        </div>

                        {/* Date */}
                        <div>
                            <label className="block text-[11px] uppercase tracking-wider mb-1.5" style={{ color: C.muted }}>
                                Shift Date
                            </label>
                            <input type="date" value={shiftDate}
                                max={toLocalDateInput(new Date())}
                                onChange={e => setShiftDate(e.target.value)}
                                className="w-full rounded-lg border px-2.5 py-2 text-[12px] outline-none"
                                style={{ borderColor: C.border, color: C.text, background: C.card }} />
                        </div>

                        {/* Shift start */}
                        <div>
                            <label className="block text-[11px] uppercase tracking-wider mb-1.5" style={{ color: C.muted }}>
                                Shift Start (PHT)
                            </label>
                            <input type="time" value={shiftStart}
                                onChange={e => setShiftStart(e.target.value)}
                                className="w-full rounded-lg border px-2.5 py-2 text-[12px] outline-none"
                                style={{ borderColor: C.border, color: C.text, background: C.card }} />
                        </div>

                        {/* Shift end */}
                        <div>
                            <label className="block text-[11px] uppercase tracking-wider mb-1.5" style={{ color: C.muted }}>
                                Shift End (PHT)
                            </label>
                            <input type="time" value={shiftEnd}
                                onChange={e => setShiftEnd(e.target.value)}
                                className="w-full rounded-lg border px-2.5 py-2 text-[12px] outline-none"
                                style={{ borderColor: C.border, color: C.text, background: C.card }} />
                        </div>
                    </div>

                    {/* Quick shift presets */}
                    <div className="flex items-center gap-2 mt-4 flex-wrap">
                        <span className="text-[11px] uppercase tracking-wider" style={{ color: C.muted }}>Quick presets:</span>
                        {[
                            { label: "Morning (6am–2pm)", start: "06:00", end: "14:00" },
                            { label: "Afternoon (2pm–10pm)", start: "14:00", end: "22:00" },
                            { label: "Closing (10pm–2am)", start: "22:00", end: "02:00" },
                        ].map(p => (
                            <button key={p.label}
                                onClick={() => { setShiftStart(p.start); setShiftEnd(p.end) }}
                                className="px-2.5 py-1 rounded-full text-[10px] border transition-colors"
                                style={{
                                    background: shiftStart === p.start && shiftEnd === p.end ? C.teal : C.card,
                                    borderColor: shiftStart === p.start && shiftEnd === p.end ? C.teal : C.border,
                                    color: shiftStart === p.start && shiftEnd === p.end ? "#fff" : C.textSoft,
                                    cursor: "pointer",
                                }}>
                                {p.label}
                            </button>
                        ))}
                    </div>
                </Card>

                {/* No branch selected */}
                {!selectedBranch && (
                    <Card>
                        <div className="flex flex-col items-center justify-center gap-3 py-12">
                            <div style={{
                                width: 52, height: 52, borderRadius: "50%",
                                background: C.amber + "15",
                                display: "flex", alignItems: "center", justifyContent: "center", fontSize: 24
                            }}>☕</div>
                            <p className="m-0 text-[14px] font-semibold" style={{ color: C.text }}>Select a branch to begin</p>
                            <p className="m-0 text-[12px]" style={{ color: C.muted }}>Choose a branch and shift window above to generate the report.</p>
                        </div>
                    </Card>
                )}

                {/* Loading state */}
                {selectedBranch && loading && (
                    <Card><Spinner height={200} /></Card>
                )}

                {/* ── Report Content ──────────────────────────────────────── */}
                {selectedBranch && !loading && (
                    <>
                        {/* Report header badge (visible in PDF) */}
                        <div className="flex items-center justify-between flex-wrap gap-2 px-1">
                            <div className="flex items-center gap-2 flex-wrap">
                                <span className="text-[11px] px-3 py-1 rounded-full font-semibold"
                                    style={{ background: C.amber + "15", color: C.gold, border: `1px solid ${C.amber}33` }}>
                                    ☕ {branchName}
                                </span>
                                <span className="text-[11px] px-3 py-1 rounded-full"
                                    style={{ background: C.dark, color: C.textSoft, border: `1px solid ${C.border}` }}>
                                    📅 {shiftDate}
                                </span>
                                <span className="text-[11px] px-3 py-1 rounded-full"
                                    style={{ background: C.dark, color: C.textSoft, border: `1px solid ${C.border}` }}>
                                    🕐 {shiftStart} – {shiftEnd} PHT
                                </span>
                            </div>
                            <span className="text-[11px]" style={{ color: C.muted }}>
                                {orders.length} order{orders.length !== 1 ? "s" : ""} in window
                            </span>
                        </div>

                        {/* ── KPI Row ──────────────────────────────────────── */}
                        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2.5">
                            <KPI label="Shift Revenue" value={fmt(totalRevenue)} sub={`${completed.length} completed orders`} color={C.green} />
                            <KPI label="Avg Ticket" value={avgTicket > 0 ? fmt(Math.round(avgTicket)) : "—"} sub="per completed order" color={C.amber} />
                            <KPI label="Total Discounts" value={fmt(totalDiscount)} sub={`across ${completed.length} orders`} color={C.teal} />
                            <KPI label="Cancelled Orders" value={cancelled.length} sub={`${orders.length > 0 ? ((cancelled.length / orders.length) * 100).toFixed(1) : 0}% cancellation rate`} color={C.coral} />
                            <KPI label="Total Orders" value={orders.length} sub={`completed + cancelled`} color={C.blue} />
                        </div>

                        {orders.length === 0 ? (
                            <Card>
                                <EmptyState message="No orders found for this branch and shift window." height={200} />
                            </Card>
                        ) : (
                            <>
                                {/* ── Hourly chart + breakdowns ─────────────── */}
                                <div className="grid grid-cols-1 lg:grid-cols-[1.4fr_1fr] gap-5">
                                    <Card>
                                        <SectionTitle title="Hourly Revenue" subtitle="Revenue by hour during this shift (PHT)" />
                                        <HourlyRevenueChart orders={orders} />
                                    </Card>
                                    <Card>
                                        <SectionTitle title="Order Breakdown" subtitle="By fulfillment type and order source" />
                                        <div className="flex flex-col gap-4">
                                            <FulfillmentBreakdown orders={orders} />
                                            <SourceBreakdown orders={orders} />
                                        </div>
                                    </Card>
                                </div>

                                {/* ── Staff activity ───────────────────────── */}
                                <Card>
                                    <SectionTitle title="Staff Activity" subtitle="Orders handled and completions per staff member during this shift" />
                                    <StaffActivityTable orders={orders} staffMap={staffMap} />
                                </Card>

                                {/* ── Order Log ────────────────────────────── */}
                                <Card>
                                    <SectionTitle title="Order Log" subtitle="Full list of orders placed during this shift" />
                                    <OrderLog orders={orders} staffMap={staffMap} />
                                </Card>

                                {/* ── Summary note ─────────────────────────── */}
                                <div className="rounded-xl p-4 border"
                                    style={{ background: C.amber + "08", borderColor: C.amber + "33" }}>
                                    <p className="m-0 text-[13px] uppercase tracking-wider mb-3 font-bold" style={{ color: C.gold }}>
                                        Shift Summary
                                    </p>
                                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-2.5">
                                        {[
                                            ["Gross Revenue (subtotal)", fmt(completed.reduce((s, o) => s + Number(o.subtotal), 0))],
                                            ["Total Discounts Applied", `−${fmt(totalDiscount)}`],
                                            ["Net Revenue Collected", fmt(totalRevenue)],
                                            ["Completed Orders", `${completed.length} of ${orders.length}`],
                                            ["Cancelled Orders", `${cancelled.length} (${orders.length > 0 ? ((cancelled.length / orders.length) * 100).toFixed(1) : 0}%)`],
                                            ["Average Ticket Size", avgTicket > 0 ? fmtFull(avgTicket) : "—"],
                                        ].map(([label, val]) => (
                                            <div key={label} className="rounded-lg p-3 border"
                                                style={{ background: C.card, borderColor: C.amber + "22" }}>
                                                <p className="m-0 text-[10px] uppercase tracking-wide" style={{ color: C.muted }}>{label}</p>
                                                <p className="m-0 mt-1 text-[14px] font-bold" style={{ color: C.text }}>{val}</p>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </>
                        )}
                    </>
                )}
            </div>
        </div>
    )
}